// @noflow
import { useNotifications } from '@/context/notifications/notifications'
import type {
  ReviewPlanChangesMutation,
  ReviewPlanChangesMutationVariables
} from '@/gql/graphql'
import { ACCOUNT_ROUTES } from '@/routes'
import { useMutation, useReactiveVar } from '@apollo/client'
import Cookies from 'js-cookie'
import upperFirst from 'lodash/upperFirst'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  dogsDataVar,
  subscriptionDataVar,
  userDataVar
} from '@/services/apollo'

import cookies from '@/utils/cookies'
import { Product, captureException } from '@/utils/sentry'

import useLocalStorage from '@/hooks/useLocalStorage'
import { useLocalisationHelper } from '@/hooks/useLocalisationHelper/useLocalisationHelper'
import { useWonderingEvent } from '@/hooks/useWonderingEvent'

import { Button } from '@/components/elements/atoms/Button'
import Card from '@/components/elements/atoms/Card/Card'
import { FixedBase } from '@/components/elements/atoms/FixedBase'
import Image from '@/components/elements/atoms/Image/Image'
import Notification from '@/components/elements/atoms/InPageNotification/InPageNotification'
import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import Separator from '@/components/elements/atoms/Separator/Separator'
import Text, { AllowedAlignment } from '@/components/elements/atoms/Text/Text'

import STYLES from './ReviewPlanChanges.module.sass'

import { REVIEW_PLAN_CHANGES_MUTATION } from './mutations/reviewPlanChangesMutation'

import { ReviewPlanChangesFragment } from './fragments/__generated__/ReviewPlanChangesFragment'

type PlanChangesLocalStorage = {
  currentPlan: ReviewPlanChangesFragment
  chosenPlan: ReviewPlanChangesFragment
}

const LOCAL_STORAGE_KEY = 'ReviewPlanChanges'

const ReviewPlanChanges = (): JSX.Element | null => {
  const namespace = 'account'
  const copyContext = 'plan_review_v3'
  const container = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const localisationHelper = useLocalisationHelper()
  const userData = useReactiveVar(userDataVar)
  const dogsData = useReactiveVar(dogsDataVar)
  const subscriptionData = useReactiveVar(subscriptionDataVar)
  const { setErrorNotification, setSuccessNotification } = useNotifications()
  const { triggerWonderingEvent } = useWonderingEvent()
  const [storedPlanChanges, setStoredPlanChanges] = useLocalStorage<
    string,
    PlanChangesLocalStorage | null
  >(LOCAL_STORAGE_KEY, null)
  const [currentPlan] = useState<ReviewPlanChangesFragment | null>(
    storedPlanChanges?.currentPlan ?? null
  )
  const [chosenPlan] = useState<ReviewPlanChangesFragment | null>(
    storedPlanChanges?.chosenPlan ?? null
  )

  const [confirmNewChosenPlan, { data, loading }] = useMutation<
    ReviewPlanChangesMutation,
    ReviewPlanChangesMutationVariables
  >(REVIEW_PLAN_CHANGES_MUTATION)

  const updatePlan = useCallback(() => {
    if (!userData?.id || !chosenPlan?.id) {
      throw new Error('User data and chosen plan id must be defined')
    }
    if (Cookies.get(cookies.reviewingMFITBPlanOffer) === 'true') {
      Cookies.set(cookies.swapToMFITBPlanOfferSeen, 'true')
      Cookies.set(cookies.reviewingMFITBPlanOffer, 'false')
    }

    const notificationCopyVariables = {
      possessiveDogName: localisationHelper.getPossessiveName(
        upperFirst(dogsData?.dogs?.[0]?.name) || ''
      ),
      count: dogsData?.dogs?.length || 0
    }

    confirmNewChosenPlan({
      variables: {
        userId: userData.id,
        planId: chosenPlan.id,
        nextNBoxesNum: 3
      },
      onCompleted: (data) => {
        const newPlan = data.subscriptionPlanUpdate?.subscription?.plan
        if (subscriptionData && newPlan) {
          subscriptionDataVar({
            ...subscriptionData,
            plan: {
              ...subscriptionData.plan,
              ...newPlan
            }
          })
        }

        if (currentPlan && newPlan) {
          triggerWonderingEvent({
            eventType: 'customer_updated_duration_new_plan_mgmt_experience',
            options: {
              condition: currentPlan.durationInWeeks !== newPlan.durationInWeeks
            }
          })

          triggerWonderingEvent({
            eventType: 'customer_updated_portion_size_new_plan_mgmt_experience',
            options: {
              condition:
                currentPlan.servingInformation.gramsPerDay !==
                newPlan.servingInformation.gramsPerDay
            }
          })
        }

        setStoredPlanChanges(null)

        setSuccessNotification({
          // eslint-disable-next-line i18next/no-literal-string
          text: `${copyContext}.alerts.success`,
          namespace,
          variables: notificationCopyVariables
        })
      },
      onError: (error) => {
        captureException('Error updating plan', {
          extra: {
            message: error
          },
          tags: {
            product: Product.Account
          }
        })

        setErrorNotification({
          // eslint-disable-next-line i18next/no-literal-string
          text: `${copyContext}.alerts.error`,
          namespace,
          variables: notificationCopyVariables
        })
      }
    })
  }, [
    currentPlan,
    chosenPlan,
    confirmNewChosenPlan,
    dogsData?.dogs,
    localisationHelper,
    setErrorNotification,
    setStoredPlanChanges,
    setSuccessNotification,
    subscriptionData,
    userData,
    triggerWonderingEvent
  ])

  const returnPath = location.state?.entryPoint ?? ACCOUNT_ROUTES.base

  const cancel = useCallback(() => {
    if (Cookies.get(cookies.reviewingMFITBPlanOffer) === 'true') {
      Cookies.set(cookies.swapToMFITBPlanOfferSeen, 'true')
      Cookies.set(cookies.reviewingMFITBPlanOffer, 'false')
    }
    navigate(returnPath, { replace: true })
  }, [navigate, returnPath])

  useEffect(() => {
    if (data) {
      navigate(returnPath, { replace: true })
    }
  }, [data, navigate, returnPath])

  if (!currentPlan || !chosenPlan) {
    throw new Error('Current plan and chosen plan must be defined')
  }

  const { pouchesPerDay, pouchSize, numberOfPouches, durationInWeeks } =
    chosenPlan
  const frequencyUpdated =
    currentPlan.durationInWeeks !== chosenPlan.durationInWeeks
  const { gramsPerDay } = chosenPlan.servingInformation

  const pouchImageSlug =
    pouchesPerDay > 1
      ? 'two-pouches'
      : pouchesPerDay < 1
      ? 'half-pouch'
      : 'one-pouch'
  const newPricePerDay = localisationHelper.formatPrice(chosenPlan.pricePerDay)
  const currentPricePerDay = localisationHelper.formatPrice(
    currentPlan.pricePerDay
  )

  const commonTextProps = {
    namespace: namespace,
    margin: false,
    align: 'left' as AllowedAlignment
  }

  const commonImageProps = {
    image: {
      width: 40,
      height: 40
    }
  }

  return (
    <div className={STYLES.container} ref={container}>
      <div className={STYLES.cardWrapper}>
        <Card mask padding={0} shadow>
          <div className={STYLES.cardContent}>
            {/* Updated grams per day */}
            <div className={STYLES.oneLineGrid}>
              <Text
                text={`${copyContext}.updated_plan_text`}
                element="h3"
                variables={{
                  count: dogsData?.dogs?.length || 0,
                  possessiveDogName: localisationHelper.getPossessiveName(
                    upperFirst(dogsData?.dogs?.[0]?.name) || ''
                  )
                }}
                {...commonTextProps}
              />
              <div className={STYLES.text}>
                <Text
                  text={`${copyContext}.grams_per_day`}
                  variant="display24"
                  bold
                  variables={{ gramsPerDay }}
                  {...commonTextProps}
                />
              </div>
              <Image
                alt={`${copyContext}.bowl_alt`}
                slug="duck-and-chicken-bowl"
                {...commonImageProps}
              />
            </div>
            <div className={STYLES.pouchFrequencyBlock}>
              <div className={STYLES.pouchLine}>
                <Image
                  alt={`${copyContext}.pouches_alt`}
                  slug={pouchImageSlug}
                  {...commonImageProps}
                />
                <Text
                  text={`${copyContext}.pouches_per_day`}
                  variant="display16"
                  variables={{
                    numberOfPouchesPerDay: pouchesPerDay,
                    pouchSize,
                    count: pouchesPerDay
                  }}
                  {...commonTextProps}
                />
              </div>
              <Separator handdrawn margin={{ bottom: 24 }} />
              <div className={STYLES.pouchCountLine}>
                <Image
                  alt={`${copyContext}.box_alt`}
                  slug="open-box-of-butternut-meals-illustration"
                  {...commonImageProps}
                />
                <div>
                  <Text
                    text={`${copyContext}.pouches_per_box`}
                    variables={{ pouchesPerBox: numberOfPouches }}
                    variant="display16"
                    {...commonTextProps}
                  />
                  <Text
                    text={`${copyContext}.cadence_in_weeks`}
                    variables={{ count: durationInWeeks }}
                    {...commonTextProps}
                  />
                </div>
              </div>
              {frequencyUpdated && (
                <SectionWrapper margin={{ top: 24, bottom: 0 }}>
                  <Notification
                    text={{
                      text: `${copyContext}.maximum_pouches_info`,
                      namespace: 'account',
                      variables: {
                        numberOfPouches: numberOfPouches
                      },
                      align: 'left'
                    }}
                    variant="info"
                  />
                </SectionWrapper>
              )}
            </div>
          </div>
          <div className={STYLES.pricingInfo}>
            {/* Updated pricing */}
            <div className={STYLES.twoLineGrid}>
              <div className={STYLES.firstLine}>
                <Text
                  text={`${copyContext}.new_price_per_day`}
                  variant="display16"
                  variables={{ newPricePerDay }}
                  {...commonTextProps}
                />
              </div>
              <div className={STYLES.secondLine}>
                <Text
                  text={`${copyContext}.current_price_per_day`}
                  variables={{ currentPricePerDay }}
                  colour="brandBlue400"
                  {...commonTextProps}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
      <FixedBase backgroundColor="brandWhite90" container={container}>
        <div className={STYLES.buttons}>
          <Button
            onClick={updatePlan}
            typography={{
              text: `${copyContext}.cta.${loading ? 'updating' : 'confirm'}`,
              namespace
            }}
            identifier="update_plan.update"
            disabled={loading}
          />
          <Button
            variant="secondary"
            onClick={cancel}
            typography={{
              text: `${copyContext}.cta.cancel`,
              namespace
            }}
            identifier="update_plan.dont_update"
            disabled={loading}
          />
        </div>
      </FixedBase>
    </div>
  )
}

export type { PlanChangesLocalStorage }
export { ReviewPlanChanges, LOCAL_STORAGE_KEY }
